import { CardData, ButtonTextData } from '@Types/common';
import React, { useRef, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { validateUrl } from '@Utils/common';
import { useGenerateShortURLMutation } from '@Pages/AutomationBuilderV0/apiSlice';



interface CardEditorProps {
  automationId: any;
  cards: CardData[];
  onCardsChange: (cards: CardData[]) => void;
  showMobileMockupCallback: (ssmm: boolean) => void;
}

const CardEditor: React.FC<CardEditorProps> = ({ automationId, cards, onCardsChange, showMobileMockupCallback }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<number>(-1);
  const [showMobileMockup, setShowMobileMockup] = useState(true);
  const swiperRef = useRef<SwiperType | null>(null);
  const [cardErrors, setCardErrors] = useState<{ [index: number]: any }>([]);

  const [generateShortURL, { isLoading: generateShortURLIsLoading }] = useGenerateShortURLMutation()

  const [tempButton, setTempButton] = useState<ButtonTextData | null>(null);
  const [tempButtonErrors, setTempButtonErrors] = useState<{ title?: string; url?: string }>({});



  useEffect(() => {
    showMobileMockupCallback(showMobileMockup);
  }, [showMobileMockup]);

  useEffect(() => {
    setTempButton(null);
    setIsEditing(-1);
  }, [activeIndex]);


  const validateCard = (card: CardData): any => {
    const errors: any = {};

    if (card.title.length > 80) errors['title'] = 'Title exceeds 80 characters';
    if (card.subtitle.length > 80) errors['subtitle'] = 'Subtitle exceeds 80 characters';
    if (card.image_url && !validateUrl(card.image_url)) errors['image'] = 'Invalid image URL';

    const buttonErrors: any[] = [];

    card.buttons.forEach((btn, i) => {
      const btnErrors: any = {};

      if (btn.title.length > 20) {
        btnErrors['title'] = `Button ${i + 1} text too long`;
      }

      if (btn.type === 'link' && btn.data && !validateUrl(btn.data)) {
        btnErrors['url'] = `Button ${i + 1} has invalid URL`;
      }

      if (Object.keys(btnErrors).length > 0) {
        buttonErrors[i] = btnErrors;
      }
    });

    if (buttonErrors.length > 0) {
      errors['button'] = buttonErrors;
    }

    return errors;
  };



  useEffect(() => {

    for (let i = 0; i < cards.length; i++) {
      const errors = validateCard(cards[i]);
      setCardErrors(prev => ({ ...prev, [i]: errors }));
    }

  }, [cards])



  const updateCardAndValidate = (index: number, newCard: CardData) => {
    const newCards = [...cards];
    newCards[index] = newCard;
    onCardsChange(newCards);
    const errors = validateCard(newCard);
    setCardErrors(prev => ({ ...prev, [index]: errors }));
  };

  const handleCardChange = (index: number, field: keyof CardData, value: string) => {
    const newCard = { ...cards[index], [field]: value };
    updateCardAndValidate(index, newCard);
  };

  const handleButtonChange = (cardIndex: number, buttons: ButtonTextData[]) => {
    const newCard = { ...cards[cardIndex], buttons };
    updateCardAndValidate(cardIndex, newCard);
  };

  const addNewCard = () => {
    const newCard: CardData = {
      title: '',
      subtitle: '',
      image_url: '',
      buttons: [],
    };
    onCardsChange([...cards, newCard]);
    setActiveIndex(cards.length);
  };

  const deleteCard = (indexToDelete: number) => {
    if (cards.length <= 1) return;
    const newCards = cards.filter((_, index) => index !== indexToDelete);
    onCardsChange(newCards);
    if (activeIndex === indexToDelete) {
      setActiveIndex(indexToDelete === 0 ? 0 : indexToDelete - 1);
    } else if (activeIndex > indexToDelete) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleCardButtonUpdate = async () => {
    if (isEditing == -1) return;
    const postData = {
      automation_id: automationId,
      original_url: cards[activeIndex]?.buttons?.[isEditing]?.data
    }

    try {
      const resp = await generateShortURL(postData).unwrap();
      if (resp) {
        const newButtons = [...cards[activeIndex]?.buttons];
        newButtons[isEditing] = { ...newButtons[isEditing], short_url: resp?.short_url };
        handleButtonChange(activeIndex, newButtons);
      }
    } catch (error: any) {
      console.error(error)
    }

    setIsEditing(-1)
  }

  const validateButton = (btn: ButtonTextData): any => {
    const errors: any = {};

    if (btn.title.length > 20) {
      errors.title = 'Button text must be ≤ 20 characters';
    } else if (btn.title.length == 0) {
      errors.title = 'Button text title is empty';
    }

    if (btn.type === 'link' && !btn.data && !validateUrl(btn.data)) {
      errors.url = 'Invalid URL';
    }

    return errors;
  };

  const handleTempButtonSave = () => {
    if (!tempButton) return;

    const validationErrors = validateButton(tempButton);
    if (Object.keys(validationErrors).length > 0) {
      setTempButtonErrors(validationErrors);
      return;
    }

    const newButtons = [...cards[activeIndex].buttons, { ...tempButton }];
    handleButtonChange(activeIndex, newButtons);
    setTempButton(null);
    setTempButtonErrors({});
    setIsEditing(-1);
  }


  return (
    <div className="space-y-4">
      {/* Toggle switch */}
      <div className="flex justify-end pr-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-600">
          Show Mobile Mockup
          <input
            type="checkbox"
            checked={showMobileMockup}
            onChange={() => setShowMobileMockup(!showMobileMockup)}
            className="toggle toggle-sm toggle-primary"
          />
        </label>
      </div>

      {/* Layout */}
      <div className="flex items-center gap-2 flex-wrap">
        {cards.map((_, index) => {
          const hasError = cardErrors[index] ? Object.keys(cardErrors[index]).length > 0 : 0;
          return (
            <div key={index} className=" flex items-center gap-1">
              <button
                onClick={() => {
                  setActiveIndex(index);
                  swiperRef.current?.slideTo(index);
                }}
                className={`relative w-8 h-8 flex items-center justify-center  ${index === activeIndex ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                {index + 1}
                {hasError && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500"></span>
                )}
              </button>

              {cards.length > 1 && (
                <button
                  onClick={() => deleteCard(index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}

        {cards.length < 10 && (
          <button
            onClick={addNewCard}
            className="w-8 h-8 bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-gray-200"
          >
            +
          </button>
        )}
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        {/* Editor */}
        <div className={`w-full ${showMobileMockup && "md:w-1/2"} space-y-4 mx-auto`}>
          {/* Navigation */}


          {/* Form */}
          <div className="space-y-4 bg-white rounded-lg p-6 border">
            <div>

              <div className="mt-2 mx-auto w-[250px] h-48 rounded-lg overflow-hidden flex items-center justify-center bg-gray-100 border">
                {cards[activeIndex]?.image_url ? (
                  <img
                    src={cards[activeIndex].image_url}
                    alt="Card preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <p className="text-gray-500">No Image URL Added</p>
                )}
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
              <input
                type="text"
                value={cards[activeIndex]?.image_url || ''}
                onChange={(e) => handleCardChange(activeIndex, 'image_url', e.target.value)}
                placeholder="Enter image URL"
                className="mt-2 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {cardErrors[activeIndex]?.image && (
                <p className="text-sm text-red-500 mt-1">Please enter a valid image URL</p>
              )}


            </div>

            {/* CARD Title */}
            <div className='relative'>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={cards[activeIndex]?.title || ''}
                onChange={(e) => handleCardChange(activeIndex, 'title', e.target.value)}
                placeholder="Enter title"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="absolute right-2 bottom-2  text-xs text-gray-400">
                {cards[activeIndex]?.title.length}/80
              </span>
              {cardErrors[activeIndex]?.title && (
                <p className="text-sm text-red-500 mt-1">Title must be ≤ 80 characters</p>
              )}

            </div>


            {/* CARD Subtitle */}
            <div className='relative'>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <input
                type="text"
                value={cards[activeIndex]?.subtitle || ''}
                onChange={(e) => handleCardChange(activeIndex, 'subtitle', e.target.value)}
                placeholder="Enter subtitle"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="absolute right-2 bottom-2  text-xs text-gray-400">
                {cards[activeIndex]?.subtitle.length}/80
              </span>
              {cardErrors[activeIndex]?.subtitle && (
                <p className="text-sm text-red-500 mt-1">Subtitle must be ≤ 80 characters</p>
              )}

            </div>

            {/* Buttons */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Buttons</label>
              {cards[activeIndex]?.buttons.map((btn, idx) => (
                <div key={idx} className="space-y-1">
                  {isEditing === idx ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center gap-2">
                          <div className="relative w-full">
                            <input
                              value={btn.title}
                              onChange={(e) => {
                                const newButtons = [...cards[activeIndex].buttons];
                                newButtons[idx] = { ...btn, title: e.target.value };
                                handleButtonChange(activeIndex, newButtons);
                              }}
                              placeholder="Enter Button Text"
                              className="py-2 px-4 pr-16 rounded-xl border-2 border-dotted h-[45px] w-full focus:outline-none focus:border-purple-400"
                              maxLength={20}
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              {btn.title.length}/20
                            </span>
                            {cardErrors[activeIndex]?.button?.[idx]?.title && (
                              <p className="text-sm text-red-500">Button text must be ≤ 20 characters</p>
                            )}
                          </div>
                          <select
                            value={btn.type}
                            onChange={(e) => {
                              const newButtons = [...cards[activeIndex].buttons];
                              newButtons[idx] = { ...btn, type: e.target.value as 'link' | 'postback' };
                              handleButtonChange(activeIndex, newButtons);
                            }}
                            className="h-[45px] px-2 rounded-xl border-2 border-dotted focus:outline-none focus:border-purple-400"
                          >
                            <option value="link">Link</option>
                            {/* <option value="message" disabled>Postback</option> */}
                          </select>
                        </div>

                      </div>
                      {btn.type === 'link' && (
                        <input
                          value={btn.data}
                          onChange={(e) => {
                            const newButtons = [...cards[activeIndex].buttons];
                            newButtons[idx] = { ...btn, data: e.target.value };
                            handleButtonChange(activeIndex, newButtons);
                          }}
                          placeholder="Enter URL"
                          className={`w-full px-3 py-2 border rounded-lg ${cardErrors[activeIndex]?.button?.[idx]?.url ? 'border-red-500' : 'focus:ring-2 focus:ring-purple-500'}`}
                        />
                      )}

                      {cardErrors[activeIndex]?.button?.[idx]?.url && (
                        <p className="text-sm text-red-500">Please enter a valid URL</p>
                      )}
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setIsEditing(-1)} className="text-gray-600">
                          Cancel
                        </button>
                        <button onClick={() => handleCardButtonUpdate()} className="bg-purple-600 text-white px-3 py-1 rounded-lg">
                          {generateShortURLIsLoading && <span className='loading_spinner'></span>}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 border rounded-lg">
                      <span>{btn.title}</span>
                      <div className="flex gap-2">
                        <button onClick={() => setIsEditing(idx)} className="text-gray-500">✎</button>
                        <button
                          onClick={() => {
                            const newButtons = cards[activeIndex].buttons.filter((_, i) => i !== idx);
                            handleButtonChange(activeIndex, newButtons);
                          }}
                          className="text-red-500"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {tempButton && (
                <div className="space-y-1">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center gap-2 w-full">
                        <div className="relative w-full">
                          <input
                            value={tempButton.title}
                            onChange={(e) =>
                              setTempButton({ ...tempButton, title: e.target.value })
                            }
                            placeholder="Enter Button Text"
                            className="py-2 px-4 pr-16 rounded-xl border-2 border-dotted h-[45px] w-full focus:outline-none focus:border-purple-400"
                            maxLength={20}
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            {tempButton.title.length}/20
                          </span>
                        </div>
                        <select
                          value={tempButton.type}
                          onChange={(e) =>
                            setTempButton({ ...tempButton, type: e.target.value as 'link' | 'postback' })
                          }
                          className="h-[45px] px-2 rounded-xl border-2 border-dotted focus:outline-none focus:border-purple-400"
                        >
                          <option value="link">Link</option>
                        </select>
                      </div>

                    </div>
                    {tempButtonErrors.title && (
                      <p className="text-sm text-red-500">{tempButtonErrors.title}</p>
                    )}

                    {tempButton.type === 'link' && (
                      <input
                        value={tempButton.data}
                        onChange={(e) => setTempButton({ ...tempButton, data: e.target.value })}
                        placeholder="Enter URL"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    )}
                    {tempButton.type === 'link' && tempButtonErrors.url && (
                      <p className="text-sm text-red-500">{tempButtonErrors.url}</p>
                    )}

                    <div className="flex justify-end gap-2">
                      <button onClick={() => {
                        setTempButton(null);
                        setIsEditing(-1);
                        setTempButtonErrors({})
                      }} className="text-gray-600">
                        Cancel
                      </button>
                      <button onClick={handleTempButtonSave} className="bg-purple-600 text-white px-3 py-1 rounded-lg">
                        Save
                      </button>
                    </div>



                  </div>
                </div>
              )}


              {cards[activeIndex]?.buttons.length < 3 && (
                <button
                  onClick={() => {
                    setTempButton({ title: '', data: '', type: 'link' });
                    setIsEditing(cards[activeIndex].buttons.length); // Index it's *going* to be

                  }}
                  disabled={isEditing != -1}
                  className="w-full py-2 px-4 border-2 border-dashed rounded-lg hover:border-purple-400 disabled:border-gray-200 disabled:bg-gray-200 transition"
                >
                  + Add Button
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Mockup */}
        {showMobileMockup && (
          <div className="w-full md:w-1/2 relative">
            <img
              src="https://app.linkdm.com/assets/images/dashboard_mobile_mockup.png"
              className="w-full relative"
              alt="Mobile Mockup"
            />
            <div className="absolute left-[30px] bottom-[20px]  w-[80%] h-[420px] rounded-[20px]">
              <Swiper
                onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
                onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.realIndex)}
                loop={false}
                spaceBetween={12}
                slidesPerView={1.45}
                slidesOffsetBefore={-40} // tweak this if needed
                centeredSlides
                onActiveIndexChange={(swiper: SwiperType) => setActiveIndex(swiper.activeIndex)}
              >
                {cards.slice(0, 10).map((card, index) => (
                  <SwiperSlide key={index}>
                    <div
                      className={`w-full h-full m-2 p-2 bg-white rounded-lg shadow ${index === activeIndex ? 'ring-2 ring-purple-500' : ''
                        }`}
                    >
                      {card.image_url ? (
                        <img src={card.image_url} alt="" className="w-full h-40 object-cover rounded-md" />
                      ) : (
                        <div className="w-full h-40 flex items-center justify-center bg-gray-100 text-sm text-gray-500 border rounded-md">
                          No Image
                        </div>
                      )}
                      <div className="mt-2">
                        <h3 className="text-sm font-semibold truncate">{card.title || "Enter Title"}</h3>
                        <p className="text-xs text-gray-500 truncate">{card.subtitle || "Enter Subtitle"}</p>
                      </div>
                      <div className="mt-2 space-y-1">
                        {card.buttons.map((btn, i) => (
                          <button
                            key={i}
                            className="w-full py-1 text-xs text-center border rounded bg-gray-100 hover:bg-gray-200 truncate"
                          >
                            {btn.title || 'Button'}
                          </button>
                        ))}
                        {card.buttons.length < 3 && (
                          <button
                            onClick={() => {
                              setTempButton({ title: '', data: '', type: 'link' });
                              setIsEditing(cards[activeIndex].buttons.length); // Index it's *going* to be

                            }}
                            className="w-full py-1 text-xs text-center border rounded bg-gray-100 hover:bg-gray-200 truncate"
                          >
                            + Add Button
                          </button>
                        )}
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>

        )}


      </div>
    </div>
  );
};

export default CardEditor;
